import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import deadlineService from '../services/deadlineService';

export const DEADLINES_KEY = ['attorney', 'deadlines'];

/**
 * Hook to fetch all deadlines
 */
export function useDeadlines(options = {}) {
  return useQuery({
    queryKey: [...DEADLINES_KEY, options],
    queryFn: () => deadlineService.getDeadlines(options),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true
  });
}

/**
 * Hook to fetch a single deadline
 */
export function useDeadline(deadlineId) {
  return useQuery({
    queryKey: [...DEADLINES_KEY, deadlineId],
    queryFn: () => deadlineService.getDeadline(deadlineId),
    enabled: !!deadlineId,
    staleTime: 1000 * 60 * 5
  });
}

/**
 * Hook to complete a deadline
 */
export function useCompleteDeadline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deadlineId, data }) => deadlineService.completeDeadline(deadlineId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEADLINES_KEY });
    }
  });
}

/**
 * Hook to update a deadline
 */
export function useUpdateDeadline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deadlineId, data }) => deadlineService.updateDeadline(deadlineId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEADLINES_KEY });
    }
  });
}

/**
 * Hook to create a deadline
 */
export function useCreateDeadline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => deadlineService.createDeadline(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEADLINES_KEY });
    }
  });
}

export default {
  useDeadlines,
  useDeadline,
  useCompleteDeadline,
  useUpdateDeadline,
  useCreateDeadline
};
